"use client";

import globeIcon from "@/assets/icons/globe-icon.webp";
import NotFound from "@/assets/images/not-found.png";
import CustomTooltip from "@/components/CustomTooltip";
import { Info } from "@phosphor-icons/react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";

export default function InternetPenetration({
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
}) {
  const searchParams = useSearchParams();
  const selectedCountryId = searchParams.get("country");

  // Get the title with country name if country is selected, otherwise region name
  const getTitle = () => {
    if (selectedCountryName) {
      return `Internet users (% of population) — ${selectedCountryName}`;
    }
    return `Internet users (% of population) — By Country`;
  };

  // Generate countries data from World Bank API data
  const generateCountriesData = () => {
    if (regionData && Array.isArray(regionData)) {
      // Region view - use real World Bank internet data
      return regionData
        .filter((country) => country.macroeconomic?.internet > 0)
        .sort(
          (a, b) =>
            (b.macroeconomic?.internet || 0) - (a.macroeconomic?.internet || 0),
        )
        .slice(0, 5) // Take top 5 countries
        .map((country) => {
          let percentage = country.macroeconomic.internet;

          // Fix 100% bug: Force 1 decimal place or cap at 99.9% if it's strictly less than 100 but displayed as 100 due to rounding
          // If it's effectively 100, we might want to keep it or cap it. The requirement says "Force 1 decimal place (e.g., 99.7%) or cap at >99.5%".
          // Let's use 1 decimal place for precision.
          // If value is > 99.5 and < 100, showing 100 might be misleading if we want to show it's not fully saturated.

          let formattedPercentage = percentage;
          if (percentage > 99.9) {
            formattedPercentage = 99.9;
          } else {
            formattedPercentage = parseFloat(percentage.toFixed(1));
          }

          return {
            name: country.country_name || "Unknown",
            percentage: formattedPercentage,
          };
        });
    } else {
      // No data available
      return [];
    }
  };

  const filtered = generateCountriesData();

  // Render Stat Card for Single Country
  if (selectedCountryId) {
    let percentage = countryData?.macroeconomic?.internet || 0;

    // Apply same fix for single country
    if (percentage > 99.9) {
      percentage = 99.9;
    } else {
      percentage = parseFloat(percentage.toFixed(1));
    }

    const hasData = countryData?.macroeconomic?.internet > 0;

    return (
      <div className='flex flex-col h-full'>
        <div className='flex items-center justify-between gap-2 mb-4'>
          <p className='text-base font-medium sm:text-sm'>{getTitle()}</p>
          <CustomTooltip
            trigger={
              <span className='-mt-2 -mr-2 text-white/40'>
                <Info className='size-5' />
              </span>
            }
            content='Individuals using the Internet (% of population), sourced from the World Bank and using the latest available year (which may vary by country).'
          />
        </div>

        {hasData ? (
          <div className='flex items-center gap-3 flex-1 pb-3.5'>
            <div className='relative size-9'>
              <Image src={globeIcon.src} layout='fill' alt='Total Funding' />
            </div>
            <span className='text-3xl font-bold'>{percentage}%</span>
          </div>
        ) : (
          <div className='p-6 py-4 my-4 text-center border border-dashed rounded-lg'>
            <div className='relative block mx-auto aspect-square size-20'>
              <Image
                objectFit='cover'
                src={NotFound.src}
                alt='NotFound Image'
                layout='fill'
                className='object-cover'
              />
            </div>
            <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
            <p className='mt-1 text-xs text-white/40'>
              No internet data available
            </p>
          </div>
        )}
        <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
          Source: World Bank
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Display title with region/country name */}
      <div className='flex items-center justify-between'>
        <h3 className='text-base font-medium sm:text-sm'>{getTitle()}</h3>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='Individuals using the Internet (% of population), sourced from the World Bank and using the latest available year (which may vary by country).'
        />
      </div>

      {filtered.length > 0 ? (
        <div className='space-y-3.5'>
          {filtered.map(({ name, percentage }) => (
            <div
              key={name}
              className='flex items-center gap-3'
              title={`${name} • ${percentage}% • Latest Year`}
            >
              {/* Hide label if one country is selected */}
              {!selectedCountryId && (
                <p className='w-20 text-xs font-medium opacity-60'>{name}</p>
              )}
              <div className='relative flex-1 h-1.5 overflow-hidden rounded-full bg-white/10'>
                <div
                  className='rounded-full absolute inset-0 bg-gradient-to-r from-[#FFD338] to-[#CD4F4F]'
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <p className='w-10 text-xs font-medium text-right opacity-50'>
                {percentage}%
              </p>
            </div>
          ))}
          <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
            Source: World Bank
          </div>
        </div>
      ) : (
        <div className='p-6 py-4 text-center border border-dashed rounded-lg'>
          <div className='relative block mx-auto aspect-square size-20'>
            <Image
              objectFit='cover'
              src={NotFound.src}
              alt='NotFound Image'
              layout='fill'
              className='object-cover'
            />
          </div>
          <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
          <p className='mt-1 text-xs text-white/40'>
            No internet data available
          </p>
        </div>
      )}
    </>
  );
}
